package com.schoolconnect.app.runtime

import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.CancellationException

data class AppErrorEvent(
    val message: String,
    val source: String = "",
    val dedupeKey: String = ""
)

object AppErrorCenter {
    private val _events = MutableSharedFlow<AppErrorEvent>(
        extraBufferCapacity = 64
    )

    val events: SharedFlow<AppErrorEvent> = _events.asSharedFlow()

    fun emit(message: String, source: String = "", dedupeKey: String = "") {
        val normalized = message.trim()
        if (normalized.isBlank()) return
        _events.tryEmit(
            AppErrorEvent(
                message = normalized,
                source = source.trim(),
                dedupeKey = dedupeKey.trim()
            )
        )
    }

    fun emit(throwable: Throwable, source: String = "", fallbackMessage: String = "Something went wrong. Please try again.") {
        if (throwable.isCancellationThrowable()) return
        emit(
            message = throwable.toAppErrorMessage(fallbackMessage),
            source = source,
            dedupeKey = throwable::class.simpleName.orEmpty()
        )
    }

    private fun Throwable.isCancellationThrowable(): Boolean {
        return this is CancellationException || cause is CancellationException
    }

    private fun Throwable.toAppErrorMessage(fallbackMessage: String): String {
        val message = message.orEmpty().trim()
        if (message.isNotBlank()) {
            return when {
                message.contains("timeout", ignoreCase = true) -> "Request timed out. Please try again."
                message.contains("unauthorized", ignoreCase = true) -> "Your session expired. Please sign in again."
                message.contains("forbidden", ignoreCase = true) -> "You do not have permission to perform this action."
                message.contains("network", ignoreCase = true) && message.contains("unreachable", ignoreCase = true) ->
                    "Unable to reach the server. Check your connection and try again."
                else -> message
            }
        }
        return fallbackMessage
    }
}
